/** tm/legal/LegalTopicEditor.tsx — 주제 1개 편집 (이름·설명·검색어[판례·해석 / 국회 법안·입법예고·국무회의 / 뉴스]·제외어·출처·핀·세부 주제·새 글 알림·삭제 확인)
 *  저장하면 서버가 약어↔정식 명칭을 자동으로 더해 준다 → 「소진공 → 소상공인시장진흥공단 자동 추가」 토스트 */
import { useEffect, useMemo, useState } from 'react';
import { useGuardReport, type SettingsGuard } from './settingsGuard';
import { C, T, IMEText } from '../components/ui';
import { btn, inputStyle, labelStyle, errMsg, MUTED } from './legalTheme';
import { createKeyword, updateKeyword, deleteKeyword } from './legalApi';
import TermChips from './TermChips';
import { SourcesPicker, PinsEditor, SubtopicsEditor, patternError } from './LegalTopicParts';
import { FEED_LABEL } from './legalTypes';
import type { KeywordInput, LegalKeyword, LegalSource, SynonymGroups, TermExpansion } from './legalTypes';

export const EMPTY_TOPIC: KeywordInput = {
  label: '', description: '', terms: [], body_terms: [], news_terms: [], news_must: [], bill_terms: [], news_exclude: [], exclude_terms: [],
  sources: null, pins: [], subtopics: [], active: true, alert: true,
};

interface Props {
  kw: LegalKeyword | null; sources: LegalSource[]; synonyms: SynonymGroups; abbr?: string[]; guard?: SettingsGuard;
  onSaved: (k: LegalKeyword) => void; onDeleted: (id: number) => void; onToast: (m: string, k?: 'ok' | 'error') => void;
}

function toInput(k: LegalKeyword | null): KeywordInput {
  if (!k) return { ...EMPTY_TOPIC };
  return {
    label: k.label, description: k.description || '', terms: k.terms || [], body_terms: k.body_terms || [],
    news_terms: k.news_terms || [], news_must: k.news_must || [], sources: k.sources ?? null,
    bill_terms: k.bill_terms || [], news_exclude: k.news_exclude || [], exclude_terms: k.exclude_terms || [],
    pins: k.pins || [], subtopics: k.subtopics || [], active: k.active !== false, alert: k.alert !== false,
  };
}

/** 자동 확장 내역 → "소진공 → 소상공인시장진흥공단 자동 추가" */
export function expandedMsg(list: TermExpansion[] | undefined): string {
  if (!list || !list.length) return '';
  const parts = list.slice(0, 3).map(x => `${x.from} → ${(Array.isArray(x.to) ? x.to : [x.to]).join('·')}`);
  return `${parts.join(', ')}${list.length > 3 ? ` 외 ${list.length - 3}건` : ''} 자동 추가`;
}
const group = { fontSize: T.sm, fontWeight: 600, color: C.sub, margin: '16px 0 8px', paddingBottom: 6, borderBottom: `1px solid ${C.border}` } as const;

function problem(f: KeywordInput, isFeed: boolean): string | null {
  if (!f.label.trim()) return '주제 이름을 입력해 주세요';
  if (!isFeed && !f.terms.length && !f.body_terms.length && !f.news_terms.length && !f.bill_terms.length) return '검색어를 하나 이상 넣어 주세요';
  if (f.news_terms.length && !f.news_must.length) return '뉴스 검색어가 있으면 뉴스 필수어도 넣어 주세요 (관련 없는 기사를 거릅니다)';
  if (f.subtopics.some(s => !s.label.trim() || patternError(s.pattern))) return '세부 주제의 이름과 패턴을 확인해 주세요';
  if (f.pins.some(p => !p.law.trim())) return '관련 조문의 법령명을 입력해 주세요';
  return null;
}

export default function LegalTopicEditor({ kw, sources, synonyms, abbr = [], guard, onSaved, onDeleted, onToast }: Props) {
  const [f, setF] = useState<KeywordInput>(() => toInput(kw));
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setF(toInput(kw)); setConfirmDel(false); setErr(''); }, [kw]);
  const isFeed = !!kw?.feed;
  const set = (p: Partial<KeywordInput>) => { setF(cur => ({ ...cur, ...p })); if (err) setErr(''); };

  const origKey = useMemo(() => JSON.stringify(toInput(kw)), [kw]);
  const dirty = JSON.stringify(f) !== origKey;
  /** 제목·본문 검색어에는 약칭(신보·기보·중진공…)을 권하지 않는다 — 법령 제목에는 정식 명칭만 나온다 */
  const noAbbr = useMemo(() => [...abbr, ...synonyms.flat().filter(w => /^[가-힣]{2,3}법$/.test(w))], [abbr, synonyms]);
  const save = async (): Promise<boolean> => {
    const bad = problem(f, isFeed);
    if (bad) { setErr(bad); return false; }
    setSaving(true);
    const body: KeywordInput = { ...f, label: f.label.trim(), description: (f.description || '').trim(), pins: f.pins.map(p => ({ law: p.law.trim(), jo: p.jo.trim(), title: p.title.trim() })) };
    const send: Partial<KeywordInput> = isFeed
      ? { label: body.label, description: body.description, pins: body.pins, subtopics: body.subtopics, active: body.active, alert: body.alert }
      : body;
    try {
      const k = kw ? await updateKeyword(kw.id, send) : await createKeyword(body);
      const { expanded, ...row } = k;
      onSaved(row); const ex = expandedMsg(expanded);
      onToast(ex ? `저장했습니다 · ${ex}` : kw ? '주제를 저장했습니다' : '주제를 추가했습니다');
      return true;
    } catch (e) { setErr(errMsg(e, '저장하지 못했습니다')); return false; }
    finally { setSaving(false); }
  };
  useGuardReport(guard, dirty, save);
  const del = async () => {
    if (!kw) return;
    try { await deleteKeyword(kw.id); onDeleted(kw.id); onToast('주제를 삭제했습니다'); }
    catch (e) { setErr(errMsg(e, '삭제하지 못했습니다')); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle} htmlFor="lw-kw-label">주제 이름</label>
          <IMEText id="lw-kw-label" value={f.label} onChange={v => set({ label: v })} placeholder="예: 창업중소기업 세액감면" style={inputStyle} />
        </div>
        <div style={{ flex: '2 1 260px' }}>
          <label style={labelStyle} htmlFor="lw-kw-desc">설명</label>
          <IMEText id="lw-kw-desc" value={f.description || ''} onChange={v => set({ description: v })} placeholder="예: 조특법 §6 · 소비성서비스업" style={inputStyle} />
        </div>
      </div>
      {isFeed ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 8, background: C.bgAlt, border: `1px solid ${C.border}`, marginBottom: 12 }}>
          <span style={{ fontSize: T.sm, fontWeight: 700, color: C.accent, background: C.accentLt, borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>자동 수집</span>
          <span style={{ fontSize: T.sm, color: C.sub, lineHeight: 1.6 }}>{FEED_LABEL[kw?.feed?.type || ''] || '검색어 없이 전체를 자동으로 모으는 주제입니다'}<br />
            <span style={{ fontSize: T.sm, color: MUTED }}>검색어 없이 모으는 주제라 검색어 칸이 없습니다. 이름·설명·조문 핀·알림만 바꿀 수 있습니다.</span></span>
        </div>
      ) : <>
        <div style={group}>판례·해석</div>
        <TermChips label="제목 검색어" hint="판례·해석 제목에서 찾습니다. 정식 명칭으로 넣으세요 (약칭은 뉴스 검색어에)" value={f.terms} onChange={v => set({ terms: v })} synonyms={synonyms} exclude={noAbbr} />
        <TermChips label="본문 검색어" hint="제목에 없어도 본문에 있으면 모읍니다 (확인 후 추가)" value={f.body_terms} onChange={v => set({ body_terms: v })} synonyms={synonyms} exclude={noAbbr} />
        <TermChips label="제외어" hint="제목에 이 말이 있으면 모으지 않습니다" value={f.exclude_terms} onChange={v => set({ exclude_terms: v })} synonyms={[]} />
        <div style={group}>국회 법안 · 입법예고 · 국무회의</div>
        <TermChips label="법안 검색어" hint="법안·입법예고·정부입법현황(국무회의) 이름에서 찾습니다. 법령명을 넣으면 그 법의 개정안이 모입니다. 새로 넣은 검색어는 다음 수집에서 지난 2년치를 한 번 채웁니다" value={f.bill_terms} onChange={v => set({ bill_terms: v })} synonyms={synonyms} exclude={noAbbr} />
        <div style={group}>뉴스</div>
        <TermChips label="뉴스 검색어" hint="약칭과 정식 명칭을 둘 다 자동으로 찾습니다" value={f.news_terms} onChange={v => set({ news_terms: v })} synonyms={synonyms} />
        <TermChips label="뉴스 필수어" hint="기사 제목에 이 중 하나는 있어야 남깁니다" value={f.news_must} onChange={v => set({ news_must: v })} synonyms={synonyms} />
        <TermChips label="뉴스 제외어" hint="기사 제목에 있으면 버립니다 (예: 농업정책자금)" value={f.news_exclude} onChange={v => set({ news_exclude: v })} synonyms={[]} />
        <div style={group}>출처 · 조문</div>
        <SourcesPicker sources={sources} value={f.sources} onChange={v => set({ sources: v })} />
      </>}
      <PinsEditor value={f.pins} onChange={v => set({ pins: v })} />
      <SubtopicsEditor value={f.subtopics} onChange={v => set({ subtopics: v })} />
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 14 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: T.sm, color: C.sub, cursor: 'pointer' }}>
          <input type="checkbox" checked={f.active} onChange={e => set({ active: e.target.checked })} /> 이 주제로 수집하기
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: T.sm, color: C.sub, cursor: 'pointer' }} title="끄면 벨·사이드바·아침 문자에 넣지 않고 목록에 회색 NEW 수만 표시합니다">
          <input type="checkbox" checked={f.alert} onChange={e => set({ alert: e.target.checked })} /> 새 글 알림 <span style={{ fontSize: T.sm, color: MUTED }}>{f.alert ? '(팝업·벨·아침 문자)' : '(끔 — 목록에 회색 숫자만)'}</span>
        </label>
      </div>
      {err && <div role="alert" style={{ fontSize: T.sm, color: C.red, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderTop: `1px solid ${C.border}`, padding: '12px 0', position: 'sticky', bottom: 0, background: C.white, zIndex: 2 }}>
        <button type="button" className="lw-f" disabled={saving} onClick={() => { save(); }} style={{ ...btn('pri'), opacity: saving ? 0.6 : 1 }}>{saving ? '저장 중…' : kw ? '저장' : '주제 추가'}</button>
        <span style={{ fontSize: T.sm, color: dirty ? '#B45309' : MUTED }}>{dirty ? '저장하지 않은 변경이 있습니다' : kw ? '저장됨' : ''}</span>
        <span style={{ flex: 1 }} />
        {kw && !confirmDel && <button type="button" className="lw-f" onClick={() => setConfirmDel(true)} style={btn('danger')}>삭제</button>}
        {kw && confirmDel && (
          <span role="group" aria-label="삭제 확인" style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: T.sm, color: C.red }}>
            「{kw.label}」을(를) 삭제할까요? 모인 글은 남습니다.
            <button type="button" className="lw-f" onClick={del} style={{ ...btn('danger'), background: C.red, color: C.white }}>삭제</button>
            <button type="button" className="lw-f" onClick={() => setConfirmDel(false)} style={btn()}>취소</button>
          </span>
        )}
      </div>
    </div>
  );
}
